import React, { useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import Placeholder from '@tiptap/extension-placeholder';
import Highlight from '@tiptap/extension-highlight';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import { Button } from '../components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '../components/ui/tooltip';
import SectionDictationButton from './SectionDictationButton';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Heading1,
  Heading2,
  Heading3,
  Quote,
  Table as TableIcon,
  Subscript as SubIcon,
  Superscript as SupIcon,
  Highlighter,
  Mic,
  MicOff,
  Loader2,
  Undo,
  Redo
} from 'lucide-react';

const MenuButton = ({ onClick, isActive, disabled, children, tooltip }) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onClick}
          disabled={disabled}
          className={`h-8 w-8 p-0 ${isActive ? 'bg-blue-100 text-blue-800' : ''}`}
          data-testid={`editor-btn-${tooltip?.toLowerCase().replace(/\s+/g, '-')}`}
        >
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

// Render-prop renderer for SectionDictationButton — extracted to module
// scope so React doesn't recreate the component on every editor render
// (lint react/no-unstable-nested-components).
const renderMicMenuButton = (sectionType) => ({ recording, processing, onClick }) => (
  <MenuButton
    onClick={processing ? undefined : onClick}
    isActive={recording}
    tooltip={
      processing
        ? 'Processing dictation…'
        : recording
          ? 'Stop & preview'
          : (sectionType === 'chief_complaints'
              ? 'Dictate patient conversation — preview before applying'
              : 'Voice Input — preview before applying')
    }
  >
    {processing ? (
      <Loader2 className="h-4 w-4 animate-spin text-violet-600" />
    ) : recording ? (
      <MicOff className="h-4 w-4 text-red-500 recording-pulse" />
    ) : (
      <Mic className="h-4 w-4" />
    )}
  </MenuButton>
);

export function RichTextEditor({ 
  value = '', 
  onChange, 
  placeholder = 'Start typing...',
  autoFocus = false,
  showToolbar = true,
  showVoiceInput = true,
  className = '',
  // sectionType: lets the dictation backend pick the right refinement
  // prompt. For 'chief_complaints' the entire patient-doctor conversation
  // is consolidated into the patient's actual complaints (not echoed
  // verbatim). For history/examination/treatment_plan the transcript is
  // gently cleaned for drug-name / unit accuracy.
  sectionType = '',
}) {
  // Track whether the last change came from the editor itself
  const isInternalChange = useRef(false);

  const handleTranscription = (text) => {
    if (editor) {
      editor.commands.insertContent(text + ' ');
    }
  };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3]
        }
      }),
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph']
      }),
      Subscript,
      Superscript,
      Placeholder.configure({
        placeholder
      }),
      Highlight.configure({
        multicolor: false
      }),
      Table.configure({
        resizable: true
      }),
      TableRow,
      TableCell,
      TableHeader,
      TaskList,
      TaskItem.configure({
        nested: true
      })
    ],
    content: value,
    autofocus: autoFocus,
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none h-full'
      },
      handlePaste: (view, event, slice) => {
        return false;
      }
    },
    onUpdate: ({ editor }) => {
      isInternalChange.current = true;
      const html = editor.getHTML();
      onChange?.(html);
    }
  });

  // Only sync external value changes (e.g., loading data, applying templates)
  // Skip when the change originated from the editor's own onUpdate
  useEffect(() => {
    if (!editor) return;
    if (isInternalChange.current) {
      isInternalChange.current = false;
      return;
    }
    if (value !== editor.getHTML()) {
      editor.commands.setContent(value, false);
    }
  }, [value, editor]);

  if (!editor) {
    return null;
  }

  return (
    <div className={`tiptap-editor flex flex-col border border-gray-200 rounded-lg overflow-hidden bg-white ${className}`} data-testid="rich-text-editor">
      {showToolbar && (
        <div className="flex flex-wrap items-center gap-1 px-2.5 py-1.5 border-b border-gray-200 bg-gray-50 flex-shrink-0" data-testid="editor-toolbar">
          {/* History */}
          <MenuButton 
            onClick={() => editor.chain().focus().undo().run()} 
            disabled={!editor.can().undo()}
            tooltip="Undo"
          >
            <Undo className="h-4 w-4" />
          </MenuButton>
          <MenuButton 
            onClick={() => editor.chain().focus().redo().run()} 
            disabled={!editor.can().redo()}
            tooltip="Redo"
          >
            <Redo className="h-4 w-4" />
          </MenuButton>

          <div className="w-px h-6 bg-gray-200 mx-1" />

          {/* Headings */}
          <MenuButton 
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            isActive={editor.isActive('heading', { level: 1 })}
            tooltip="Heading 1"
          >
            <Heading1 className="h-4 w-4" />
          </MenuButton>
          <MenuButton 
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            isActive={editor.isActive('heading', { level: 2 })}
            tooltip="Heading 2"
          >
            <Heading2 className="h-4 w-4" />
          </MenuButton>
          <MenuButton 
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            isActive={editor.isActive('heading', { level: 3 })}
            tooltip="Heading 3"
          >
            <Heading3 className="h-4 w-4" />
          </MenuButton>

          <div className="w-px h-6 bg-gray-200 mx-1" />

          {/* Text formatting */}
          <MenuButton 
            onClick={() => editor.chain().focus().toggleBold().run()}
            isActive={editor.isActive('bold')}
            tooltip="Bold"
          >
            <Bold className="h-4 w-4" />
          </MenuButton>
          <MenuButton 
            onClick={() => editor.chain().focus().toggleItalic().run()}
            isActive={editor.isActive('italic')}
            tooltip="Italic"
          >
            <Italic className="h-4 w-4" />
          </MenuButton>
          <MenuButton 
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            isActive={editor.isActive('underline')}
            tooltip="Underline"
          >
            <UnderlineIcon className="h-4 w-4" />
          </MenuButton>
          <MenuButton 
            onClick={() => editor.chain().focus().toggleHighlight().run()}
            isActive={editor.isActive('highlight')}
            tooltip="Highlight"
          >
            <Highlighter className="h-4 w-4" />
          </MenuButton>

          <div className="w-px h-6 bg-gray-200 mx-1" />

          {/* Sub/Superscript */}
          <MenuButton 
            onClick={() => editor.chain().focus().toggleSubscript().run()}
            isActive={editor.isActive('subscript')}
            tooltip="Subscript"
          >
            <SubIcon className="h-4 w-4" />
          </MenuButton>
          <MenuButton 
            onClick={() => editor.chain().focus().toggleSuperscript().run()}
            isActive={editor.isActive('superscript')}
            tooltip="Superscript"
          >
            <SupIcon className="h-4 w-4" />
          </MenuButton>

          <div className="w-px h-6 bg-gray-200 mx-1" />

          {/* Lists */}
          <MenuButton 
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            isActive={editor.isActive('bulletList')}
            tooltip="Bullet List"
          >
            <List className="h-4 w-4" />
          </MenuButton>
          <MenuButton 
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            isActive={editor.isActive('orderedList')}
            tooltip="Numbered List"
          >
            <ListOrdered className="h-4 w-4" />
          </MenuButton>

          <div className="w-px h-6 bg-gray-200 mx-1" />

          {/* Alignment */}
          <MenuButton 
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
            isActive={editor.isActive({ textAlign: 'left' })}
            tooltip="Align Left"
          >
            <AlignLeft className="h-4 w-4" />
          </MenuButton>
          <MenuButton 
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
            isActive={editor.isActive({ textAlign: 'center' })}
            tooltip="Align Center"
          >
            <AlignCenter className="h-4 w-4" />
          </MenuButton>
          <MenuButton 
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
            isActive={editor.isActive({ textAlign: 'right' })}
            tooltip="Align Right"
          >
            <AlignRight className="h-4 w-4" />
          </MenuButton>

          <div className="w-px h-6 bg-gray-200 mx-1" />

          {/* Quote & Table */}
          <MenuButton 
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            isActive={editor.isActive('blockquote')}
            tooltip="Quote"
          >
            <Quote className="h-4 w-4" />
          </MenuButton>
          <MenuButton 
            onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
            tooltip="Insert Table"
          >
            <TableIcon className="h-4 w-4" />
          </MenuButton>

          {/* Voice Input — preview-first flow (2-stage: Whisper → AI refine) */}
          {showVoiceInput && (
            <>
              <div className="w-px h-6 bg-gray-200 mx-1" />
              <SectionDictationButton
                sectionType={sectionType}
                onApply={handleTranscription}
                buttonComponent={renderMicMenuButton(sectionType)}
              />
            </>
          )}
        </div>
      )}
      
      <div className="flex-1 overflow-auto px-3 py-1.5 min-h-0">
        <EditorContent editor={editor} className="h-full" />
      </div>
    </div>
  );
}
